package dev.lingvo.site.pages.app.projects.slug

import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.components.marketing.formatThousands
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

/**
 * The one control the page recommends, and where it goes.
 *
 * Separate from the composable so the branching is testable without a DOM: which button is loudest
 * is the entire wayfinding an empty project gets, and it used to be decided by whether a target
 * language existed — true the moment somebody picks German at creation, and nothing to do with
 * whether there is anything to translate.
 */
data class PrimaryAction(
    val label: String,
    val path: String,
    val enabled: Boolean = true,
    /**
     * Whether pressing this reserves credits.
     *
     * **The overview matched on `label == "Pre-translate"`** to pick the sparkle icon and to decide
     * whether to print the remaining balance beside the button. `label` is display copy: wrapping it
     * in `t()` — which it needs — would silently swap the icon and delete the reassurance the
     * comment beside it argues is required at the point of spend. Identity belongs on a field
     * nobody is going to translate.
     */
    val spendsCredits: Boolean = false,
)

/**
 * Which button is loudest, ranked for the owner-translator (`PRODUCT.md` Users, amended
 * 2026-08-25): the reader owns the repository and translates by machine with their own provider
 * keys, so the machine run is the action and the editor is where they go afterwards to review it.
 *
 * `role` matters because pre-translate is gated ADMIN (`requireSpendAllowed`, `MtRoutes.kt:889`):
 * a translator has no machine run to be pointed at, so promoting a button they cannot press would
 * be the exact failure the amended principle 3 names — refuse honestly, do not offer and refuse.
 * For them the manual editor stays primary, which is what this function already did before this
 * role split existed.
 */
fun primaryAction(keyCount: Int, slug: String, firstTarget: String?, role: Role?): PrimaryAction? = when {
    role == null -> null
    keyCount == 0 && role.atLeast(Role.ADMIN) -> PrimaryAction(
        tr("app.projects.slug.project_overview_model.action_import_first_file", "Import the first file"),
        "/app/projects/$slug/data",
    )
    keyCount == 0 -> null
    firstTarget == null && role.atLeast(Role.ADMIN) -> PrimaryAction(
        tr("app.projects.slug.project_overview_model.action_add_target_language", "Add a target language"),
        "/app/projects/$slug/settings?tab=languages",
    )
    firstTarget == null -> null
    role.atLeast(Role.ADMIN) -> PrimaryAction(
        tr("app.projects.slug.project_overview_model.action_pretranslate", "Pre-translate"),
        "/app/projects/$slug/pretranslate",
        spendsCredits = true,
    )
    else -> PrimaryAction(
        tr("app.projects.slug.project_overview_model.action_translate", "Translate"),
        "/app/projects/$slug/translate/$firstTarget",
    )
}

internal fun overviewSetupGuidance(role: Role?): String = when (role) {
    Role.TRANSLATOR -> tr(
        "app.projects.slug.project_overview_model.guidance_translator",
        "You can review this project now and translate once strings and a target language exist.",
    )
    Role.VIEWER -> tr(
        "app.projects.slug.project_overview_model.guidance_viewer",
        "You can review this project, while setup and translation remain read-only.",
    )
    null -> tr(
        "app.projects.slug.project_overview_model.guidance_unknown",
        "You can review available project information, while setup and translation remain read-only.",
    )
    Role.OWNER,
    Role.ADMIN,
    -> tr(
        "app.projects.slug.project_overview_model.owner_can_finish_setup",
        "An owner or admin can finish this project's setup.",
    )
}

/**
 * What this project is, in one line, in counts.
 *
 * Replaces a chip, two badges and a relative timestamp. The key count was stated three times above
 * the fold — in the badge, in "412 keys each", and inside the readiness summary — and the number a
 * developer opens this page for, how many are left, was in none of them.
 */
/** Beyond this many target languages the subtitle names a count instead of listing them. */
const val STATE_LINE_TARGET_CAP = 3

/**
 * The direction clause alone — what this project translates from and into, and nothing else.
 *
 * For the page whose header already carries the counts as stat tiles. The overview's subtitle read
 * "en -> German, French, Japanese +1 more - 45 keys - 100 left" while the tile row 20px beneath it
 * said "45 Keys" and "100 Untranslated": the same two numbers twice in one eyeful, and "100 left"
 * against "100 Untranslated" is two words for one fact, so the reader has to check whether they are
 * the same number. The tiles are the version the spec asks for, and the version that links.
 * [stateLine] keeps its count clauses for the callers that have no tiles.
 */
fun projectDirectionLine(sourceLocale: String, targets: List<String>): String =
    directionClause(sourceLocale, targets)

fun projectDirectionLine(project: ProjectDetailDto): String = projectDirectionLine(
    sourceLocale = project.sourceLocale,
    targets = project.locales.filterNot { it == project.sourceLocale },
)

/**
 * **Capped, and named rather than coded.** Unbounded, thirty target locales turned this subtitle
 * into a three-line paragraph of comma-separated codes that pushed "Release readiness" — the thing
 * this page exists to answer — below the fold on a laptop. The server already makes this judgement
 * for its own readiness sentence, naming at most two locales before switching to "N more languages
 * are behind"; this applies the same rule. Names, because every other place on the page resolves a
 * locale to its language and this was the one printing raw codes.
 */
private fun directionClause(sourceLocale: String, targets: List<String>): String {
    val named = targets.map { code -> localeInfo(code).englishName.takeIf { it != code } ?: code }
    return when {
        targets.isEmpty() -> sourceLocale
        named.size <= STATE_LINE_TARGET_CAP -> trf(
            "app.projects.slug.project_overview_model.direction",
            "{source} → {targets}",
            "source" to sourceLocale,
            "targets" to named.joinToString(", "),
        )
        // The overflow count is its own message rather than a suffix glued on, because "+3 more"
        // is a sentence fragment in English and a different construction in most other languages.
        else -> trf(
            "app.projects.slug.project_overview_model.direction_capped",
            "{source} → {targets} +{overflow} more",
            "source" to sourceLocale,
            "targets" to named.take(STATE_LINE_TARGET_CAP).joinToString(", "),
            "overflow" to named.size - STATE_LINE_TARGET_CAP,
        )
    }
}

fun stateLine(sourceLocale: String, targets: List<String>, keyCount: Int, remaining: Int): String {
    val direction = directionClause(sourceLocale, targets)
    // `{keys}` is a counted phrase built by `tPlural` and handed in whole, so a translator writes
    // the key count once, in the language's own plural forms, and the state line reads it as a
    // value. Nesting the count inside each of these instead would ask for the same plural rule
    // four times and let three of them drift.
    val keys = tPlural(
        "app.projects.slug.project_overview_model.count.key",
        keyCount,
        "{count} key",
        "{count} keys",
    )
    return when {
        keyCount == 0 -> trf(
            "app.projects.slug.project_overview_model.state_no_keys",
            "{direction} · no keys yet",
            "direction" to direction,
        )
        targets.isEmpty() -> trf(
            "app.projects.slug.project_overview_model.state_no_target",
            "{direction} · {keys} · no target language yet",
            "direction" to direction,
            "keys" to keys,
        )
        remaining == 0 -> trf(
            "app.projects.slug.project_overview_model.state_all_translated",
            "{direction} · {keys} · all translated",
            "direction" to direction,
            "keys" to keys,
        )
        else -> trf(
            "app.projects.slug.project_overview_model.state_remaining",
            "{direction} · {keys} · {remaining} left",
            "direction" to direction,
            "keys" to keys,
            "remaining" to remaining,
        )
    }
}

/**
 * How many keys across every target language still have no translation.
 *
 * `LocaleProgressDto.translated` already includes approved — `LanguageRow` coerces on that basis
 * (`Index.kt:311`) — so remaining is `total - translated`, not `total - translated - approved`.
 * A locale with no progress row yet counts its whole key set as remaining.
 */
fun remainingKeys(project: ProjectDetailDto): Int =
    project.locales
        .filterNot { it == project.sourceLocale }
        .sumOf { code ->
            val p = project.progress.firstOrNull { it.locale == code }
            ((p?.total ?: project.keyCount) - (p?.translated ?: 0)).coerceAtLeast(0)
        }

/**
 * The target languages that still have strings with no translation at all.
 *
 * **The scope of "Pre-translate" on this page, stated as languages rather than as "everything".**
 * The header's split button prices one run over every language that is behind, so the set has to be
 * the same set [remainingKeys] counts — the number beside the button and the languages inside the
 * run are then two readings of one fact rather than two independent guesses. A language with no
 * progress row yet is behind by its whole key set, which is what an untouched locale is.
 *
 * Source is excluded: it is the language the strings are written in, not one to translate into.
 */
fun behindLocales(project: ProjectDetailDto): List<String> =
    project.locales
        .filterNot { it == project.sourceLocale }
        .filter { code ->
            val progress = project.progress.firstOrNull { it.locale == code }
            ((progress?.total ?: project.keyCount) - (progress?.translated ?: 0)) > 0
        }

fun stateLine(project: ProjectDetailDto): String = stateLine(
    sourceLocale = project.sourceLocale,
    targets = project.locales.filterNot { it == project.sourceLocale },
    keyCount = project.keyCount,
    remaining = remainingKeys(project),
)

/**
 * The command this project is one step away from, and why.
 *
 * The web app sits inside a git workflow rather than beside one, and the person reading this page
 * got here from a terminal and will go back to one. The page used to record their last `push` as
 * "Import · 2 minutes ago" — no count, no locale, no actor — and offered nothing about the `pull`
 * that ends the loop.
 */
data class TerminalHint(val command: String, val note: String)

fun terminalHint(slug: String, remaining: Int, keyCount: Int): TerminalHint = when {
    // The command is never translated. It is typed into a shell, and a translated verb is a
    // command that does not exist — which is why `TerminalHint` is kept off the extractor's
    // positional list, where its first field would read as ordinary prose.
    keyCount == 0 -> TerminalHint(
        "lingvo --project $slug push",
        tr(
            "app.projects.slug.project_overview_model.hint_no_keys",
            "No keys yet. Push your localization files to create them.",
        ),
    )
    remaining == 0 -> TerminalHint(
        "lingvo --project $slug pull",
        tPlural(
            "app.projects.slug.project_overview_model.hint_all_translated",
            keyCount,
            "All {count} key is translated and ready to pull.",
            "All {count} keys are translated and ready to pull.",
        ),
    )
    else -> TerminalHint(
        "lingvo --project $slug pull",
        tPlural(
            "app.projects.slug.project_overview_model.hint_remaining",
            remaining,
            "{count} key is still untranslated. Pull anyway to get what is done.",
            "{count} keys are still untranslated. Pull anyway to get what is done.",
        ),
    )
}

/**
 * The one line of substance an activity row can carry, or null when it has none.
 *
 * A row used to be a type label and a relative time — `Import · 2 minutes ago` — which is the same
 * sentence whatever happened. The counts were already in `payload`; nothing read them.
 *
 * Zeros are dropped because an import reports five numbers and usually only one is non-zero, and
 * an import that changed nothing says so in words: an empty detail reads as missing data rather
 * than as a settled result.
 */
fun activityDetail(type: String, payload: JsonObject): String? {
    fun text(key: String): String? =
        (payload[key] as? JsonPrimitive)?.takeIf { it.isString }?.content?.takeIf(String::isNotBlank)

    fun count(key: String): Int? = (payload[key] as? JsonPrimitive)?.content?.toIntOrNull()

    val language = text("locale")?.let { code ->
        localeInfo(code).englishName.takeIf { it != code } ?: code
    }

    val parts = mutableListOf<String>()
    language?.let(parts::add)

    when (type) {
        "FILE_IMPORTED", "TMX_IMPORTED" -> {
            // The count and its word are one phrase per translator rather than a number glued to an
            // English participle: "38 obsolete" is an adjective in English and a verb in half the
            // languages this product ships in.
            val changes = listOfNotNull(
                count("added")?.takeIf { it > 0 }?.let {
                    trf("app.projects.slug.project_overview_model.import_added", "{count} added", "count" to it)
                },
                count("updated")?.takeIf { it > 0 }?.let {
                    trf("app.projects.slug.project_overview_model.import_updated", "{count} updated", "count" to it)
                },
                count("obsolete")?.takeIf { it > 0 }?.let {
                    trf("app.projects.slug.project_overview_model.import_obsolete", "{count} obsolete", "count" to it)
                },
                count("skipped")?.takeIf { it > 0 }?.let {
                    trf("app.projects.slug.project_overview_model.import_skipped", "{count} skipped", "count" to it)
                },
            )
            // `unchanged` is deliberately not listed: it is the absence of a change, and printing
            // it beside real changes invites reading it as one.
            parts += if (changes.isEmpty()) {
                tr("app.projects.slug.project_overview_model.import_nothing_changed", "nothing changed")
            } else {
                changes.joinToString(", ")
            }
        }
        "MT_PRETRANSLATED", "MACHINE_TRANSLATION_SAVED" -> text("provider")?.let(parts::add)
        // **A cancelled run is a refund, and the refund is the number somebody wants.** The row used
        // to read "Machine translation cancelled" and stop there, which says nothing about the
        // credits the reader is actually asking after.
        "MT_JOB_CANCELLED" -> {
            val refunded = count("refundedCredits")
            parts += if (refunded == null || refunded <= 0) {
                tr("app.projects.slug.project_overview_model.mt_cancelled_no_refund", "nothing to refund")
            } else {
                tPlural(
                    "app.projects.slug.project_overview_model.count.credits_refunded",
                    refunded,
                    "{count} credit refunded",
                    "{count} credits refunded",
                )
            }
        }
        // Which way the review went. "Reviewed" alone is the one word that does not distinguish the
        // two outcomes a reviewer can produce.
        "TRANSLATION_REVIEWED" -> when (text("action")) {
            "APPROVE" -> parts += tr("app.projects.slug.project_overview_model.review_approved", "approved")
            "REQUEST_CHANGES" -> parts += tr("app.projects.slug.project_overview_model.review_sent_back", "sent back")
            else -> Unit
        }
    }

    return parts.takeIf { it.isNotEmpty() }?.joinToString(" · ")
}

/**
 * Whether the page should collapse to a single empty state.
 *
 * A project with no keys has nothing to say in four sections, and saying it four times produced
 * three differently-worded buttons pointing at one destination.
 */
fun showsOnlyEmptyState(keyCount: Int): Boolean = keyCount == 0

/**
 * What is left of the only metered resource in the product, stated beside the control that spends
 * it.
 *
 * Zero is not an error and must not be dressed as one: `PRODUCT.md` guarantees that running out
 * never blocks translating by hand, and this sentence is where that guarantee is stated, not
 * merely implied by the absence of an error.
 */
fun creditNote(remaining: Int): String = when {
    remaining <= 0 -> tr(
        "app.projects.slug.project_overview_model.no_credits_left",
        "No credits left this month. Translating by hand still works.",
    )
    else -> tPlural(
        "app.projects.slug.project_overview_model.credits_left",
        remaining,
        "{amount} credit left this month.",
        "{amount} credits left this month.",
        "amount" to formatThousands(remaining),
    )
}
